import React from 'react';
import { X, Sparkles, Bug, Shield, Wrench, Rocket } from 'lucide-react';

interface ChangelogEntry {
  version: string;
  date: string;
  highlights: string;
  changes: { type: 'feat' | 'fix' | 'security' | 'chore'; text: string }[];
}

const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.75.3',
    date: '06.04.2026',
    highlights: 'Admin Panel Bugfixes, Premium Celebration, Copecart Webhook Fix',
    changes: [
      {
        type: 'feat',
        text: 'Premium Celebration: Confetti-Animation + 5 Feature-Benefits nach Kauf',
      },
      {
        type: 'feat',
        text: 'Rechtstexte: Vorschau jetzt direkt editierbar mit Copy & Paste Formatierung',
      },
      { type: 'fix', text: 'Admin Preise & Fragen: "supabase is not defined" — fehlender Import' },
      {
        type: 'fix',
        text: 'CRITICAL: Rules of Hooks Violation — useEffect vor conditional returns',
      },
      {
        type: 'fix',
        text: 'Infinite Loading: Supabase Lock Timeout 1000ms → 500ms + 10s Safety Timeout',
      },
      {
        type: 'fix',
        text: 'Copecart Webhook: buyer_email, transaction_amount, event_type korrekt gemappt',
      },
      { type: 'fix', text: 'PRS (Dari) Locale: Doppelter Emoji-Key entfernt (479 → 478 Keys)' },
    ],
  },
  {
    version: '1.74.0',
    date: '06.04.2026',
    highlights: 'In-App Support, E-Mail CTAs, Admin Support-Dashboard',
    changes: [
      {
        type: 'feat',
        text: 'In-App Support Modal: Kategorieauswahl, Screenshot-Upload, Auto-Ticket',
      },
      { type: 'feat', text: 'Admin Support-Dashboard: Ticket-Übersicht mit Bild-Vorschau' },
      { type: 'feat', text: 'E-Mail CTA Deep-Links: ?tab=profile, ?page=feedback Routing' },
      { type: 'fix', text: 'URL-Params in allen Auth-Pfaden durchgereicht' },
      { type: 'fix', text: 'Admin Screenshot-Anzeige: metadata Feld im Ticket-Interface' },
    ],
  },
  {
    version: '1.73.0',
    date: '06.04.2026',
    highlights: 'Archive & Delete für Reports, Bulk-Aktionen, E-Mail UX',
    changes: [
      { type: 'feat', text: 'Archive + Delete für Reports & Image Feedback (Admin)' },
      { type: 'feat', text: 'Bulk-Select für Reports & Images mit Massenaktionen' },
      { type: 'feat', text: 'Auto-Archive bei Resolve-Aktion' },
      { type: 'fix', text: '4 UX-Verbesserungen: E-Mail-Gruppen, Sprachreihenfolge, Toast-Style' },
    ],
  },
  {
    version: '1.71.0',
    date: '05.04.2026',
    highlights: 'Rechtstexte-Versionierung, KI-Übersetzung, Storage Health',
    changes: [
      {
        type: 'feat',
        text: 'Legal Content: Versionierung, Übersetzungsstatus, "Alle Sprachen übersetzen"',
      },
      { type: 'feat', text: 'Versionshistorie mit Wiederherstellen-Funktion' },
      {
        type: 'fix',
        text: 'Storage Health Check: supabase.storage.listBuckets() statt custom RPC',
      },
      { type: 'fix', text: 'Platform Maintenance: 6 Fixes (Lock-Timeout, CSP, Logo, RLS)' },
    ],
  },
  {
    version: '1.70.0',
    date: '05.04.2026',
    highlights: 'Hindi + Farsi — 9 Sprachen, DB-konfigurierbares AI-Limit',
    changes: [
      { type: 'feat', text: 'Hindi (हिन्दी) + Farsi (فارسی) komplett: UI, Fragen, Legal, E-Mails' },
      { type: 'feat', text: 'AI-Limit jetzt DB-konfigurierbar statt hardcoded (Admin Settings)' },
      { type: 'feat', text: 'E-Mail Templates für Hindi und Farsi (18 Templates × 2 Sprachen)' },
      { type: 'fix', text: 'DB-Labels für neue Sprachen in language_translation_status' },
    ],
  },
  {
    version: '1.60.0',
    date: '04.04.2026',
    highlights: 'Content QA Dashboard, Bild-Audit, Kapitel-Essences',
    changes: [
      {
        type: 'feat',
        text: 'Content QA Dashboard: Fragen-Review, Übersetzungs-Check, Bilder-Audit',
      },
      { type: 'feat', text: 'Chapter Essences: KI-generierte Kapitelzusammenfassungen' },
      { type: 'feat', text: 'Image Review Pipeline: Upload + AI-basierte Qualitätsprüfung' },
      {
        type: 'feat',
        text: 'Fragen-Kategorisierung nach dem Schema der theoretischen Führerscheinprüfung',
      },
    ],
  },
  {
    version: '1.50.0',
    date: '03.04.2026',
    highlights: 'ETOS E-Mail System, Pricing Plans CRUD, Comparison Page',
    changes: [
      { type: 'feat', text: 'ETOS: 18 E-Mail Templates × 7 Sprachen, Cron-gesteuerte Trigger' },
      { type: 'feat', text: 'Pricing Plans CRUD: Admin kann Pläne verwalten + Copecart-Links' },
      { type: 'feat', text: 'Vergleichsseite: Fahryo vs. Konkurrenz mit SVG-Charts' },
      { type: 'feat', text: 'E-Mail Compliance-Check: DSGVO-konformer Footer, Abmeldelinks' },
    ],
  },
  {
    version: '1.40.0',
    date: '02.04.2026',
    highlights: 'Sentry Alerts, PWA Improvements, Marketing AI',
    changes: [
      {
        type: 'feat',
        text: 'Sentry Alert Rules: Automatische Benachrichtigung bei Fehlerrate > 5%',
      },
      { type: 'feat', text: 'PWA: App-Badge, Background Sync Vorbereitung, Manifest-Update' },
      { type: 'feat', text: 'Marketing AI: Automatisierte Content-Generierung für Social Media' },
      { type: 'fix', text: 'Admin Auth Proxy: CORS + express.json() für POST-Requests' },
    ],
  },
  {
    version: '1.37.0',
    date: '28.03.2026',
    highlights: 'Ukrainisch + Urdu + Dari — 7 Sprachen, DSGVO E-Mails',
    changes: [
      { type: 'feat', text: 'Ukrainisch (🇺🇦), Urdu (🇵🇰), Dari (🇦🇫) hinzugefügt → 7 Sprachen' },
      { type: 'feat', text: 'DSGVO-konforme E-Mail-Vorlagen: Welcome, Streak, Purchase, Security' },
      { type: 'feat', text: 'Legal Content pro Sprache: Impressum, Datenschutz, AGB, Cookies' },
      {
        type: 'security',
        text: 'Session-Management gehärtet: Token-Refresh, SSR-Workaround entfernt',
      },
    ],
  },
  {
    version: '1.35.0',
    date: '27.03.2026',
    highlights: 'Kaufdaten-Aufbewahrung, Storage-Cleanup, Speicherdauer',
    changes: [
      {
        type: 'feat',
        text: 'Kaufdaten: Anonymisierung statt Löschung (SHA-256 Hash, §257 HGB 10-Jahres-Aufbewahrung)',
      },
      {
        type: 'feat',
        text: 'Storage-Cleanup: Avatare & Uploads werden bei Kontolöschung gelöscht',
      },
      { type: 'feat', text: 'Datenschutz §18: Detaillierte Speicherdauer-Tabelle pro Dienst' },
      { type: 'fix', text: 'Hinweis bei Kontolöschung: „sofort und unwiderruflich" Info-Box' },
    ],
  },
  {
    version: '1.34.0',
    date: '27.03.2026',
    highlights: 'DSGVO & EU AI Act Compliance, +30 Tests',
    changes: [
      {
        type: 'feat',
        text: 'DSGVO Art. 17: Konto-Löschung API mit Kaskadenlöschung (8 Tabellen + Auth)',
      },
      { type: 'feat', text: 'DSGVO Art. 20: Vollständiger Datenexport aus 8 Tabellen als JSON' },
      {
        type: 'feat',
        text: 'ProfileView: Server-seitiger Export & Doppel-Bestätigung bei Löschung',
      },
      {
        type: 'security',
        text: '4 RLS Policies gehärtet: USING(true) → auth.role() = service_role',
      },
      {
        type: 'feat',
        text: '+30 Compliance-Tests: Art. 17, Art. 20, Consent, Legal, EU AI Act, Security',
      },
    ],
  },
  {
    version: '1.33.8',
    date: '27.03.2026',
    highlights: 'E-Mail Template Editor, Marketing Bugfix, +161 Tests',
    changes: [
      {
        type: 'feat',
        text: 'E-Mail Template Editor: DB-Tabelle, Admin-UI mit Live-Preview, Test-Send, Variablen-Editor',
      },
      {
        type: 'feat',
        text: 'E-Mails jetzt DB-gesteuert: {{variable}} und {{cta:Text:URL}} Syntax',
      },
      {
        type: 'feat',
        text: '+161 neue Tests (326 total): AI-Coach, Payments, Progress, Feedback, Leaderboard, PWA, Admin',
      },
      {
        type: 'fix',
        text: 'Marketing: Tabellenreferenz marketing_templates → prompt_templates korrigiert',
      },
      {
        type: 'fix',
        text: 'Marketing: Validierungsdetails werden bei Fehler angezeigt (statt nur "Ungültige Eingabe")',
      },
    ],
  },
  {
    version: '1.33.7',
    date: '26.03.2026',
    highlights: 'WebP-Migration: 87% Speicherersparnis',
    changes: [
      {
        type: 'feat',
        text: 'Storage: 726 Bilder von PNG → WebP Q90 konvertiert (~1.1 GB → ~146 MB)',
      },
      { type: 'feat', text: 'Alle questions.image_url Referenzen auf .webp aktualisiert' },
      { type: 'chore', text: 'tmp-webp/ zu .gitignore hinzugefügt' },
    ],
  },
  {
    version: '1.33.6',
    date: '26.03.2026',
    highlights: 'Test-Suite Hardening, React-Prop Fix',
    changes: [
      { type: 'fix', text: 'AuthView.test.tsx: React DOM Warning für fullWidth Prop behoben' },
      { type: 'chore', text: 'Alle 158 Tests verifiziert und stabilisiert' },
    ],
  },
  {
    version: '1.33.5',
    date: '26.03.2026',
    highlights: 'TS-Lint Cleanup, Badge Type, Feedback Detail Views, Version Sync',
    changes: [
      {
        type: 'fix',
        text: 'Badge Type: nameKey/descKey → name/description Mapping in apiClient.ts',
      },
      { type: 'fix', text: 'marketing.ts: 4× req.params.id String-Cast (TS2345 behoben)' },
      {
        type: 'fix',
        text: 'Version Sync: tests/setup.ts 2.0.0 → 1.33.3, CHANGELOG.md 2.0.0 → 1.14.0',
      },
      { type: 'feat', text: 'Feedback Admin: Klick-Detail für Reports + Bild-Feedback Tabs' },
      { type: 'feat', text: 'Changelog: v1.33.1–1.33.5 Einträge in ChangelogModal + CHANGELOG.md' },
    ],
  },
  {
    version: '1.33.4',
    date: '26.03.2026',
    highlights: 'Bug Fix Sprint 2 — 6 Produktions-Bugs',
    changes: [
      { type: 'fix', text: 'Report Versand: user_id + metadata JSONB Spalte (RLS Fix)' },
      { type: 'fix', text: 'Rangliste: Alle API-Felder (realName, gender, badges) durchgereicht' },
      { type: 'fix', text: 'Chat Menü: Öffnet nach oben (bottom-full) mit z-[60]' },
      { type: 'fix', text: 'Lern-Symbole: Kleinere Icons, engerer Abstand, kein Logo-Overlap' },
    ],
  },
  {
    version: '1.33.3',
    date: '26.03.2026',
    highlights: 'Bug Fix Sprint 3 — TS-Lint Cleanup, Badge Type Fix, Version Sync',
    changes: [
      {
        type: 'fix',
        text: 'Badge Type: nameKey/descKey → name/description Mapping in apiClient.ts',
      },
      { type: 'fix', text: 'marketing.ts: 4× req.params.id String-Cast (TS2345 behoben)' },
      { type: 'fix', text: 'Version Sync: tests/setup.ts 2.0.0 → 1.33.3 (passt zu package.json)' },
      { type: 'feat', text: 'Feedback Admin: 3-Tab-View (Beta / Reports / Bild-Feedback)' },
    ],
  },
  {
    version: '1.33.2',
    date: '26.03.2026',
    highlights: 'Bug Fix Sprint 2 — 6 Produktion-Bugs behoben',
    changes: [
      { type: 'fix', text: 'Report Versand: user_id + metadata JSONB Spalte (RLS Fix)' },
      { type: 'fix', text: 'Rangliste: Alle API-Felder (realName, gender, badges) durchgereicht' },
      { type: 'fix', text: 'Chat Menü: Öffnet nach oben (bottom-full) mit z-[60]' },
      { type: 'fix', text: 'Lern-Symbole: Kleinere Icons, engerer Abstand, kein Logo-Overlap' },
    ],
  },
  {
    version: '1.33.1',
    date: '26.03.2026',
    highlights: 'Bug Fix Sprint 1 — Premium Modal, KI Coach, Feedback',
    changes: [
      { type: 'fix', text: 'Premium Modal: 3-Monats-Plan als Vorauswahl (kein Sprung)' },
      { type: 'fix', text: 'KI Coach: Reconnect-Logik bei Verbindungsverlust' },
      { type: 'fix', text: 'Problem-Button: Dauerhaft sichtbar beim Lernen' },
      { type: 'fix', text: 'Beta-Feedback: Regelmäßige Verbindungsprüfung' },
    ],
  },
  {
    version: '1.33.0',
    date: '26.03.2026',
    highlights: 'IQC Bild-Regenerierung mit Vorher/Nachher-Vergleich, CDN-Cache-Fix',
    changes: [
      { type: 'feat', text: 'Bildprüfung Queue: "Neu generieren" Button mit optimiertem Prompt' },
      { type: 'feat', text: 'Vorher/Nachher Bildvergleich nach Regenerierung (alt vs neu)' },
      { type: 'feat', text: 'Größere Vorschaubilder in Prüf-Queue (max 400×300px)' },
      {
        type: 'fix',
        text: 'CDN-Cache Fix: Neues Bild mit versioniertem Pfad statt identischer URL',
      },
    ],
  },
  {
    version: '1.32.0',
    date: '26.03.2026',
    highlights: 'Backlog-Scan: Kapitelfilter + Live-Vorschau',
    changes: [
      { type: 'feat', text: 'Kapitelauswahl: Backlog-Scan gezielt pro Kapitel (1-14) oder alle' },
      {
        type: 'feat',
        text: 'Live-Vorschau: Aktuelles Bild + Fragentext während des Scans sichtbar',
      },
      { type: 'feat', text: 'Ergebnis-Feed: Letzte 10 geprüfte Bilder mit Score + Verdict' },
      {
        type: 'fix',
        text: 'Route-Ordering Bug: /backlog wurde von /:questionId abgefangen → silent failure',
      },
    ],
  },
  {
    version: '1.31.0',
    date: '26.03.2026',
    highlights: 'AI Image Quality Checker (IQC) — automatisierte Bildprüfung',
    changes: [
      { type: 'feat', text: 'IQC: Vollautomatische Bildprüfung mit Gemini 2.5 Pro (Vertex AI)' },
      { type: 'feat', text: 'Backlog-Scan: Bestehende Bilder prüfen (Rate-Limit: 10s)' },
      { type: 'feat', text: 'Memory-System: Fehler-Muster pro Kapitel → optimierte Prompts' },
      { type: 'feat', text: 'Dashboard mit KPIs, Kapitel-Statistiken und Fortschrittsbalken' },
      { type: 'feat', text: 'Prüf-Queue mit manueller Freigabe/Ablehnung' },
    ],
  },
  {
    version: '1.14.0',
    date: '25.03.2026',
    highlights:
      'Major Refactoring — App.tsx 3317→561 Zeilen, Security Hardening, Unified Versioning',
    changes: [
      {
        type: 'feat',
        text: 'App.tsx Monolith in 9 separate Page-Komponenten aufgeteilt (83% Reduktion)',
      },
      {
        type: 'feat',
        text: 'Neue Dateien: AuthView, LearnView, QuestionSession, ExamResultView, LeaderboardView, ProfileView, ChatView, ReportIssueModal, PasswordStrengthBar',
      },
      {
        type: 'feat',
        text: 'Health-Endpoint /api/health gibt jetzt Version aus package.json zurück',
      },
      { type: 'feat', text: 'KI-Coach 2.0: Schwächen-Analyse + proaktive Coaching-Instruktionen' },
      { type: 'security', text: '2FA Admin mit TOTP + 8 Recovery-Codes' },
      { type: 'security', text: 'Zod Input-Validierung auf allen 17 API-Endpunkten' },
      { type: 'security', text: 'safeError() auf allen 9 Route-Modulen, CSP via helmet()' },
      { type: 'fix', text: '25 doppelte i18n-Keys entfernt, 711 EN-Fragen Option-IDs migriert' },
      { type: 'chore', text: 'Versionierung: Single Source of Truth aus package.json' },
    ],
  },
  {
    version: '1.13.2',
    date: '25.03.2026',
    highlights: 'Admin 2FA Login-Fix, Cloud Scheduler Non-Blocking, Auto-Close Modal',
    changes: [
      {
        type: 'fix',
        text: 'Admin Proxy: express.json() hinzugefügt — POST-Requests von admin.fahryo.de funktionierten nicht',
      },
      {
        type: 'fix',
        text: '2FA Setup Modal: Auto-Close nach erfolgreicher Aktivierung (1.5s Delay)',
      },
      {
        type: 'fix',
        text: 'CI/CD: Cloud Scheduler Step ist jetzt non-blocking (continue-on-error)',
      },
    ],
  },
  {
    version: '1.13.0',
    date: '25.03.2026',
    highlights: 'Admin 2FA (TOTP) — QR-Code Setup, Recovery Codes, Login-Gate',
    changes: [
      { type: 'feat', text: 'TOTP 2FA: Setup mit QR-Code + 8 Recovery-Codes' },
      { type: 'feat', text: '2FA Login-Gate: 6-stelliger Code nach Supabase-Login erforderlich' },
      { type: 'feat', text: '2FA Deaktivierung: Nur mit gültigem Code möglich' },
      { type: 'feat', text: 'SecurityPage: Live 2FA-Status, Setup-Button, Deaktivierung' },
      { type: 'feat', text: 'Backend: 5 neue API-Endpunkte (/api/admin/2fa/*)' },
      { type: 'security', text: 'DB: admin_totp Tabelle mit RLS-Policy (nur eigener User)' },
    ],
  },
  {
    version: '1.12.0',
    date: '25.03.2026',
    highlights: 'Zod Input-Validierung auf allen 17 API-Endpunkten',
    changes: [
      {
        type: 'security',
        text: 'Zod-Schemas: String-Limits, UUID-Validierung, Typ-Checks auf allen POST/PUT/DELETE-Routen',
      },
      {
        type: 'feat',
        text: 'Middleware: validate.ts — wiederverwendbar, stripped unknown fields, 400 bei Fehler',
      },
      {
        type: 'feat',
        text: 'Schemas: 15 Zod-Schemas in schemas.ts (AI, Admin, Push, Feedback, Marketing, Payments)',
      },
    ],
  },
  {
    version: '1.11.0',
    date: '25.03.2026',
    highlights: 'Cloud Scheduler für Push-Cron — kein setInterval mehr',
    changes: [
      { type: 'feat', text: 'Google Cloud Scheduler: Push-Cron täglich 18:00 Europe/Berlin' },
      { type: 'feat', text: 'requireAdminOrCronAuth Middleware: Admin-JWT oder X-Cron-Secret' },
      { type: 'fix', text: 'setInterval Push-Cron entfernt — instabil bei Container-Restarts' },
      {
        type: 'chore',
        text: 'GitHub Actions: Scheduler-Job wird automatisch erstellt/aktualisiert',
      },
    ],
  },
  {
    version: '1.10.0',
    date: '25.03.2026',
    highlights: 'Deep Sentry Integration — captureException in allen 8 Backend-Routen',
    changes: [
      {
        type: 'feat',
        text: 'Sentry captureException in ai.ts, payments.ts, push.ts, marketing.ts, admin.ts, feedback.ts, stats.ts, leaderboard.ts',
      },
      { type: 'chore', text: 'Sentry DSN als Docker Build ARG + Cloud Run Env Var in CI/CD' },
    ],
  },
  {
    version: '1.9.0',
    date: '25.03.2026',
    highlights: 'Sentry Error Monitoring — Client + Server + alle 8 Backend-Routen',
    changes: [
      {
        type: 'feat',
        text: 'Sentry React SDK: Browser Tracing, Session Replay bei Fehlern (100%)',
      },
      { type: 'feat', text: 'Sentry Node SDK: Express Error Handler in server.ts' },
      { type: 'feat', text: 'ErrorBoundary: Sentry.captureException() mit React Component Stack' },
      {
        type: 'feat',
        text: 'Deep Backend: Sentry in ai, payments, push, leaderboard, stats, feedback, marketing, admin',
      },
      {
        type: 'chore',
        text: 'CI/CD: VITE_SENTRY_DSN als Docker Build ARG + SENTRY_DSN als Cloud Run Env Var',
      },
      { type: 'chore', text: 'GitHub Secrets: SENTRY_DSN + VITE_SENTRY_DSN hinzugefügt' },
    ],
  },
  {
    version: '1.8.0',
    date: '25.03.2026',
    highlights: 'Vollständige i18n — Badges, Begrüßung, Coach, Consent, Leaderboard',
    changes: [
      { type: 'feat', text: '7 Badge-Namen + Beschreibungen in DE/EN/TR übersetzt' },
      { type: 'feat', text: 'Badge-System refactored: nameKey/descKey statt hardcoded Strings' },
      { type: 'feat', text: 'Begrüßung: "Hallo" → t(\'Hallo\') (Hello/Merhaba)' },
      { type: 'feat', text: "Coach Welcome: Interpolation t('coach_welcome', { name })" },
      { type: 'feat', text: 'Consent Banner: 4 Keys × 3 Sprachen' },
      { type: 'feat', text: 'Leaderboard Filter: 5 Keys × 3 Sprachen (Gesamt/Monat/Woche)' },
      { type: 'feat', text: '+60 neue i18n-Keys über alle 3 Sprachen' },
    ],
  },
  {
    version: '1.7.0',
    date: '25.03.2026',
    highlights: 'Performance: API Caching + Bundle -40%',
    changes: [
      {
        type: 'feat',
        text: 'Cache-Service: In-Memory Map mit TTL für /pricing, /leaderboard, /health, /vapid-key',
      },
      { type: 'feat', text: 'X-Cache Header: MISS/HIT für Debugging' },
      { type: 'feat', text: 'Code Splitting: PremiumModal lazy-loaded (8 KB separater Chunk)' },
      { type: 'feat', text: 'Vendor Chunks: recharts (289 KB) + idb-keyval (2 KB) separat' },
      { type: 'feat', text: 'Main Bundle: 745 KB → 452 KB (−40%)' },
    ],
  },
  {
    version: '1.6.0',
    date: '25.03.2026',
    highlights: 'Rangliste, Leaderboard API & Stats Route',
    changes: [
      { type: 'feat', text: 'Ranglisten-Tab mit Zeitfilter (Gesamt/Monat/Woche)' },
      { type: 'feat', text: 'Leaderboard API: GET /api/leaderboard + /my-rank' },
      { type: 'feat', text: 'Stats Route: server/routes/stats.ts für Admin Dashboard' },
    ],
  },
  {
    version: '1.5.0',
    date: '25.03.2026',
    highlights: 'Security Hardening & Architektur-Refactoring',
    changes: [
      {
        type: 'security',
        text: 'CSP-Header: Helmet.js mit Content-Security-Policy, HSTS, X-Frame-Options',
      },
      { type: 'security', text: 'Push-Auth: POST /api/push/send erfordert requireAdminAuth' },
      { type: 'security', text: 'Error-Sanitization: 23 Endpunkte mit safeError()' },
      { type: 'feat', text: 'server.ts von 1784 auf ~160 Zeilen reduziert (8 Route-Module)' },
      { type: 'feat', text: 'Middleware: requireAuth, requireAdminAuth, safeError ausgelagert' },
    ],
  },
  {
    version: '1.4.0',
    date: '24.03.2026',
    highlights: 'Infrastructure Hardening, Rate Limiting & Domain Setup',
    changes: [
      { type: 'feat', text: 'Admin ErrorBoundary: Crash → Fehlerseite statt Whitescreen' },
      {
        type: 'feat',
        text: 'API Rate Limiting: 3-stufig (General 100/15min, AI 10/15min, Auth 20/15min)',
      },
      { type: 'feat', text: 'GCP Budget Alert: €50/Monat mit Alerts bei 50%, 80%, 100%' },
      { type: 'feat', text: 'admin.fahryo.de: Custom Domain mit SSL-Zertifikat live' },
      { type: 'feat', text: 'Resend SMTP: Domain-Verifizierung (DKIM, SPF, DMARC) für fahryo.de' },
      { type: 'security', text: 'RLS Policies optimiert: 11 doppelte Policies konsolidiert' },
      { type: 'fix', text: 'Admin Build: VITE_SUPABASE_ANON_KEY wird jetzt korrekt injiziert' },
      { type: 'fix', text: 'Deploy: Supabase Secrets als Build-Env-Vars an Cloud Build übergeben' },
    ],
  },
  {
    version: '1.3.0',
    date: '24.03.2026',
    highlights: 'Changelog Modal, Onboarding-Fix & Admin Crash behoben',
    changes: [
      { type: 'feat', text: 'Changelog Modal bei Klick auf Versionsnummer (Dashboard + Sidebar)' },
      {
        type: 'fix',
        text: 'Onboarding Sprachwechsel DE→EN Race Condition (async changeLanguage + remount key)',
      },
      {
        type: 'fix',
        text: 'Admin Panel Crash: React Hooks Reihenfolge (useState nach early return)',
      },
    ],
  },
  {
    version: '1.2.1',
    date: '24.03.2026',
    highlights: 'Auth-Kompatibilität & AI Client Fix',
    changes: [
      {
        type: 'fix',
        text: 'Admin Auth: Anon JWT Key statt Publishable Key für Server-Kompatibilität',
      },
      { type: 'fix', text: 'AI Client: Vertex AI priorisiert statt API Key (Spending Cap 429)' },
    ],
  },
  {
    version: '1.2.0',
    date: '24.03.2026',
    highlights: 'CI/CD Pipeline, Monitoring & Admin Deploy',
    changes: [
      { type: 'feat', text: 'GitHub Actions CI/CD Pipeline für automatische Deployments' },
      { type: 'feat', text: 'Semantic Release für automatische Versionierung' },
      { type: 'feat', text: 'Admin Panel als separater Cloud Run Service deployed' },
      { type: 'feat', text: 'API Proxy im Admin Server für Produktion' },
      { type: 'feat', text: 'Cloud Run Monitoring & Alerting (Fehlerrate + Latenz)' },
      { type: 'feat', text: 'GCP Service Account + GitHub Secret automatisch eingerichtet' },
      { type: 'security', text: 'Beta Feedback RLS Policy eingeschränkt auf eigene E-Mail' },
    ],
  },
  {
    version: '1.1.0',
    date: '24.03.2026',
    highlights: 'Versionierung, 6 Bugfixes & Dashboard-Korrektur',
    changes: [
      {
        type: 'feat',
        text: 'Versionsanzeige in Admin Panel (Sidebar + Dashboard) und Client App (Footer)',
      },
      {
        type: 'fix',
        text: 'Dashboard Fragen-Zähler: image_url statt image_placeholder (zeigte 731 statt 3)',
      },
      { type: 'fix', text: 'Exam Simulator: handleNext/handleSubmit + korrektes Scoring' },
      { type: 'fix', text: 'Kapitel-Titel: Hardcoded Deutsch → i18n Keys' },
      { type: 'fix', text: 'Premium Modal: Vollständige i18n-Refaktorierung (34 Keys)' },
      { type: 'fix', text: 'Onboarding-Sprache wird bei Login nicht mehr zurückgesetzt' },
      { type: 'fix', text: 'Logo Z-Index auf Auth-Seite korrigiert' },
    ],
  },
  {
    version: '1.0.1',
    date: '23.03.2026',
    highlights: 'Sicherheit, vollständige i18n & UX-Verbesserungen',
    changes: [
      { type: 'feat', text: 'Vollständige englische Übersetzungen (alle verbleibenden Strings)' },
      { type: 'feat', text: 'Coach EN Persona, Onboarding Language-First, Auth Sprachwechsel' },
      { type: 'feat', text: 'Instant Language Switch ohne Page Reload' },
      { type: 'feat', text: 'Mehrsprachiger Fragen-Editor, JSON Export/Import' },
      { type: 'feat', text: 'Auth Sprachwechsel UX, Admin Sprach-Management' },
      { type: 'feat', text: 'Instagram Marketing: Post-Editing, Hashtag-Prompts, Bildgenerierung' },
      {
        type: 'fix',
        text: 'Onboarding Name-Feld, Responsive Design, Badge-Kontrast, E-Mail Redirect',
      },
      { type: 'fix', text: 'Onboarding Sprachwechsel via Key-Remount + direct i18n.t()' },
      { type: 'fix', text: 'Sprache persistiert via localStorage + nach Onboarding' },
      { type: 'fix', text: 'Logo-Höhe liest jetzt immer aus DB, Cache 30s + Re-Fetch' },
      {
        type: 'security',
        text: 'Kritische RLS Policies korrigiert, Function search_path gesichert',
      },
      { type: 'security', text: 'Rate-Limit für i18n-Endpunkte' },
    ],
  },
  {
    version: '1.0.0',
    date: '19.03.2026',
    highlights: 'Erster Production Release',
    changes: [
      { type: 'feat', text: 'Fahryo App mit 731 Fragen, 14 Kapiteln' },
      { type: 'feat', text: 'KI-Coach mit Gemini AI Integration' },
      { type: 'feat', text: 'Admin Panel mit Dashboard, Bild-Management, Marketing' },
      { type: 'feat', text: 'Google & E-Mail Authentifizierung' },
      { type: 'feat', text: 'PWA-Support für iOS und Android' },
      { type: 'feat', text: 'Gemini AI Prompt-Optimierung für Bildgenerierung' },
      { type: 'feat', text: 'Privacy Consent und Supabase Integration' },
    ],
  },
];

const typeConfig = {
  feat: { icon: Sparkles, label: 'Neu', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  fix: { icon: Bug, label: 'Fix', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  security: { icon: Shield, label: 'Sicherheit', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  chore: { icon: Wrench, label: 'Wartung', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangelogModal: React.FC<Props> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 600,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          border: '1px solid var(--border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Rocket size={18} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18 }}>Changelog</h2>
              <span className="text-xs text-muted">Versionshistorie & Updates</span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Changelog entries */}
        <div style={{ overflowY: 'auto', padding: '16px 24px' }}>
          {CHANGELOG.map((entry, i) => (
            <div key={entry.version} style={{ marginBottom: i < CHANGELOG.length - 1 ? 24 : 0 }}>
              {/* Version header */}
              <div className="flex items-center gap-3" style={{ marginBottom: 8 }}>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 8,
                    background:
                      i === 0 ? 'linear-gradient(135deg, #3B82F6, #8B5CF6)' : 'var(--background)',
                    color: i === 0 ? '#fff' : 'var(--text)',
                    border: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  v{entry.version}
                </span>
                <span className="text-xs text-muted">{entry.date}</span>
                {i === 0 && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: 'rgba(34,197,94,0.1)',
                      color: '#22C55E',
                    }}
                  >
                    AKTUELL
                  </span>
                )}
              </div>
              <p
                className="text-sm"
                style={{ color: 'var(--text-muted)', marginBottom: 10, fontStyle: 'italic' }}
              >
                {entry.highlights}
              </p>

              {/* Changes list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {entry.changes.map((change, j) => {
                  const cfg = typeConfig[change.type];
                  const Icon = cfg.icon;
                  return (
                    <div key={j} className="flex items-center gap-2" style={{ fontSize: 13 }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          background: cfg.bg,
                          color: cfg.color,
                          minWidth: 70,
                          justifyContent: 'center',
                        }}
                      >
                        <Icon size={10} /> {cfg.label}
                      </span>
                      <span>{change.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Divider */}
              {i < CHANGELOG.length - 1 && (
                <div style={{ borderBottom: '1px solid var(--border)', marginTop: 20 }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
